import React, { useState, useEffect, useRef } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  FaBan,
  FaCalendarAlt,
  FaChevronDown,
  FaInfoCircle,
  FaTimes,
  FaTrash,
  FaExclamationTriangle,
} from "react-icons/fa";
import BottomNav from "../common/BottomNav";
import AppHeader from "../common/AppHeader";
import Spinner from "../common/Spinner";
import Popup from "../common/Popup";
import {
  addServiceAvailabilityException,
  getAvailabilityExceptions,
  deleteAvailabilityException,
} from "../../services/availabilityService";
import { getEventServicesBySpIdApi } from "../../services/eventService";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styles from "../css/MarkBusy.module.css";

// Date formatting utility function
const formatDate = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Custom Dropdown Component
function CustomDropdown({ value, onChange, options, placeholder, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className={styles.customDropdown} ref={dropdownRef}>
      <div
        className={`${styles.dropdownHeader} ${disabled ? styles.disabled : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={value ? styles.selectedText : styles.placeholderText}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <FaChevronDown
          className={`${styles.dropdownIcon} ${isOpen ? styles.rotated : ""}`}
        />
      </div>

      {isOpen && !disabled && (
        <div className={styles.dropdownList}>
          {options.map((option, index) => (
            <div
              key={index}
              className={`${styles.dropdownItem} ${option.value === value ? styles.selected : ""} ${option.disabled ? styles.disabledItem : ""}`}
              onClick={() => {
                if (!option.disabled) {
                  onChange(option.value);
                  setIsOpen(false);
                }
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Confirmation Dialog Component
function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  isDeleting,
}) {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.confirmDialog}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.confirmIcon}>
          <FaExclamationTriangle size={48} color="#f59e0b" />
        </div>
        <h3 className={styles.confirmTitle}>{title}</h3>
        <p className={styles.confirmMessage}>{message}</p>
        <div className={styles.confirmActions}>
          <button
            className={styles.confirmCancelButton}
            onClick={onClose}
            disabled={isDeleting}
          >
            {cancelText}
          </button>
          <button
            className={styles.confirmDeleteButton}
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className={styles.spinner}></div>
                Deleting...
              </>
            ) : (
              <>
                <FaTrash size={14} />
                {confirmText}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Info Modal Component
function InfoModal({ isOpen, onClose, title, children, actions }) {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{title}</h3>
          <button className={styles.modalClose} onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className={styles.modalBody}>{children}</div>
        {actions && <div className={styles.modalActions}>{actions}</div>}
      </div>
    </div>
  );
}

// View Busy Modal Component (Read-only with delete option)
function ViewBusyModal({ isOpen, onClose, busyDate, onDelete }) {
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    await onDelete();
    setDeleting(false);
    setShowConfirm(false);
  };

  if (!isOpen || !busyDate) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose}>
        <div
          className={styles.modalContent}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.modalHeader}>
            <h3 className={styles.modalTitle}>
              <FaInfoCircle size={16} style={{ marginRight: "8px" }} />
              {t("MarkBusy.busy_details", "Busy Mark Details")}
            </h3>
            <button className={styles.modalClose} onClick={onClose}>
              <FaTimes />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.infoContent}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  {t("MarkBusy.date", "Date")}
                </span>
                <span className={styles.infoValue}>{busyDate.date}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  {t("MarkBusy.reason", "Reason")}
                </span>
                <span className={styles.infoValue}>
                  {busyDate.reason || "No reason provided"}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  {t("MarkBusy.from", "From")}
                </span>
                <span className={styles.infoValue}>
                  {new Date(busyDate.unavailableFrom).toLocaleString("en-US", {
                    timeZone: "UTC",
                  })}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  {t("MarkBusy.to", "To")}
                </span>
                <span className={styles.infoValue}>
                  {new Date(busyDate.unavailableTo).toLocaleString("en-US", {
                    timeZone: "UTC",
                  })}
                </span>
              </div>
            </div>
          </div>
          <div className={styles.modalActions}>
            <button
              className={styles.deleteButton}
              onClick={handleDeleteClick}
              disabled={deleting}
              style={{ flex: 1 }}
            >
              <FaTrash size={14} />
              {t("MarkBusy.remove_busy", "Remove Busy")}
            </button>
            <button
              className={styles.cancelButton}
              onClick={onClose}
              disabled={deleting}
              style={{ flex: 1 }}
            >
              {t("MarkBusy.close", "Close")}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmDelete}
        title={t("MarkBusy.confirm_delete_title", "Remove Busy Mark?")}
        message={t(
          "MarkBusy.confirm_delete_message",
          "Are you sure you want to remove this busy mark? This action cannot be undone.",
        )}
        confirmText={t("MarkBusy.confirm_remove_button", "Remove Busy")}
        cancelText={t("MarkBusy.cancel", "Cancel")}
        isDeleting={deleting}
      />
    </>
  );
}

export default function MarkBusy() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState("add"); // "overview" or "add"
  const [allBusyDates, setAllBusyDates] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [selectedDateInOverview, setSelectedDateInOverview] = useState(new Date());
  const [unavailableDate, setUnavailableDate] = useState(null);
  const [reasonType, setReasonType] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serviceType, setServiceType] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [busyDates, setBusyDates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    title: "",
    content: null,
  });
  const [viewBusyModal, setViewBusyModal] = useState({
    isOpen: false,
    busyDate: null,
  });

  // Popup state
  const [popup, setPopup] = useState(null);

  const showPopup = (message, type = "success") => {
    setPopup({ message, type });
  };

  const closePopup = () => {
    setPopup(null);
  };

  const showInfoModal = (title, content) => {
    setInfoModal({ isOpen: true, title, content });
  };

  const closeInfoModal = () => {
    setInfoModal({ isOpen: false, title: "", content: null });
  };

  const showViewBusyModal = (busyDate) => {
    setViewBusyModal({ isOpen: true, busyDate });
  };

  const closeViewBusyModal = () => {
    setViewBusyModal({ isOpen: false, busyDate: null });
  };

  // Fetch all services and their busy dates on mount
  useEffect(() => {
    fetchAllServicesAndBusyDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllServicesAndBusyDates = async () => {
    setLoadingOverview(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user.spId || "";

      // Fetch all event services
      const response = await getEventServicesBySpIdApi(userId);
      
      if (response.success && response.data) {
        const items = Array.isArray(response.data) ? response.data : [];

        const formattedServices = items.map((item) => ({
          id: item.esId,
          name: item.businessName || "Event Service",
          type: "EVENT",
          category: item.subCategory?.split(",")[0] || "",
          image: item.serviceImageUrls?.split(",")[0] || "",
        }));

        setAllServices(formattedServices);

        // Fetch busy dates for all services
        const allBusyDatesArray = [];
        for (const service of formattedServices) {
          try {
            const exceptionsResponse = await getAvailabilityExceptions("EVENT", service.id);
            if (exceptionsResponse?.responseCode === 200) {
              const exceptions = exceptionsResponse.responseData || [];
              exceptions.forEach((exception) => {
                let fromDate = exception.unavailableFrom;
                let toDate = exception.unavailableTo;

                if (!fromDate.endsWith("Z") && !fromDate.includes("+") && !fromDate.includes("-", 10)) {
                  fromDate = fromDate + "Z";
                }
                if (!toDate.endsWith("Z") && !toDate.includes("+") && !toDate.includes("-", 10)) {
                  toDate = toDate + "Z";
                }

                allBusyDatesArray.push({
                  date: formatDate(new Date(fromDate)),
                  serviceId: service.id,
                  serviceName: service.name,
                  reason: exception.reason,
                  unavailableFrom: fromDate,
                  unavailableTo: toDate,
                  exceptionId: exception.id,
                });
              });
            }
          } catch (error) {
            console.error(`Error fetching busy dates for service ${service.id}:`, error);
          }
        }

        setAllBusyDates(allBusyDatesArray);
      }
    } catch (error) {
      console.error("Error fetching services and busy dates:", error);
    } finally {
      setLoadingOverview(false);
    }
  };

  // Fetch vehicles when service type changes
  useEffect(() => {
    if (serviceType) {
      fetchVehiclesByServiceType(serviceType);
    } else {
      setVehicles([]);
      setSelectedVehicle("");
      setBusyDates([]);
      setBookings([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  // Fetch busy dates and bookings when vehicle is selected
  useEffect(() => {
    async function fetchVehicleData() {
      if (!selectedVehicle) {
        setBusyDates([]);
        setBookings([]);
        return;
      }

      try {
        const vehicle = JSON.parse(selectedVehicle);

        // Fetch busy dates for EVENT services
        const apiType = vehicle.type;
        await fetchBusyDates(vehicle, apiType);

        // Fetch bookings for this vehicle
        await fetchBookingsForVehicle(vehicle);
      } catch (error) {
        console.error("Error fetching vehicle data:", error);
        setBusyDates([]);
        setBookings([]);
      }
    }

    fetchVehicleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle]);

  const fetchBusyDates = async (vehicle, apiType) => {
    try {
      const response = await getAvailabilityExceptions(apiType, vehicle.id);

      if (response?.responseCode === 200) {
        const exceptions = response.responseData || [];

        const busyDatesList = exceptions.map((exception) => {
          let fromDate = exception.unavailableFrom;
          let toDate = exception.unavailableTo;

          if (
            !fromDate.endsWith("Z") &&
            !fromDate.includes("+") &&
            !fromDate.includes("-", 10)
          ) {
            fromDate = fromDate + "Z";
          }
          if (
            !toDate.endsWith("Z") &&
            !toDate.includes("+") &&
            !toDate.includes("-", 10)
          ) {
            toDate = toDate + "Z";
          }

          return {
            date: formatDate(new Date(fromDate)),
            serviceType: exception.serviceType,
            serviceId: exception.serviceId,
            reason: exception.reason,
            unavailableFrom: fromDate,
            unavailableTo: toDate,
            exceptionId: exception.id,
          };
        });

        setBusyDates(busyDatesList);
      } else {
        setBusyDates([]);
      }
    } catch (error) {
      console.error("Error fetching busy dates:", error);
      setBusyDates([]);
    }
  };

  const fetchVehiclesByServiceType = async (type) => {
    setLoadingVehicles(true);
    setVehicles([]);
    setSelectedVehicle("");

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user.spId || "";

      if (type === "EVENT") {
        // Event services
        const response = await getEventServicesBySpIdApi(userId);
        
        if (response.success && response.data) {
          const items = Array.isArray(response.data) ? response.data : [];

          const formattedVehicles = items.map((item) => ({
            id: item.esId,
            name: item.businessName || "Event Service",
            brand: item.subCategory?.split(",")[0] || "",
            type: type,
            category: "Event",
            image: item.serviceImageUrls?.split(",")[0] || "",
            displayName: `${item.esId} - ${item.businessName || "Event Service"}`,
          }));

          setVehicles(formattedVehicles);
          setLoadingVehicles(false);
          return;
        }
        
        setVehicles([]);
        setLoadingVehicles(false);
        return;
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setVehicles([]);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const fetchBookingsForVehicle = async (vehicle) => {
    try {
      // No bookings for EVENT services in this context
      setBookings([]);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setBookings([]);
    }
  };

  const busyDateStrings = busyDates.map((bd) => bd.date);

  const getBookingDatesForVehicle = () => {
    return bookings.map((b) => {
      // Handle different date field names for different service types
      const dateField = b.pickupDateTime || b.eventDate || b.bookingDate;
      if (dateField) {
        const pickupDate = new Date(dateField);
        return formatDate(pickupDate);
      }
      return null;
    }).filter(Boolean);
  };

  const getBusyDatesForVehicle = () => {
    return busyDateStrings;
  };

  function tileClassNameModal({ date, view }) {
    if (view === "month") {
      const d = formatDate(date);
      const vehicleBookingDates = getBookingDatesForVehicle();
      const vehicleBusyDates = getBusyDatesForVehicle();

      if (vehicleBookingDates.includes(d)) {
        return "has-booking-disabled";
      }

      if (vehicleBusyDates.includes(d)) {
        return "already-busy-disabled";
      }

      if (unavailableDate && formatDate(unavailableDate) === d) {
        return "selected-unavailable";
      }
    }
    return null;
  }

  const tileDisabled = ({ date, view }) => {
    if (view === "month" && selectedVehicle) {
      const d = formatDate(date);
      const vehicleBookingDates = getBookingDatesForVehicle();
      return vehicleBookingDates.includes(d);
    }
    return false;
  };

  const handleDateClickInModal = (date) => {
    const d = formatDate(date);
    const vehicleBookingDates = getBookingDatesForVehicle();
    const vehicleBusyDates = getBusyDatesForVehicle();

    if (vehicleBookingDates.includes(d)) {
      const booking = bookings.find((b) => {
        const dateField = b.pickupDateTime || b.eventDate || b.bookingDate;
        return dateField && formatDate(new Date(dateField)) === d;
      });
      
      if (booking) {
        // Event service booking details
        const bookingContent = (
          <div className={styles.infoContent}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>
                {t("MarkBusy.booking_id", "Booking ID")}
              </span>
              <span className={styles.infoValue}>
                {booking.bookingId || booking.esBookingId || "N/A"}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>
                {t("MarkBusy.customer", "Customer")}
              </span>
              <span className={styles.infoValue}>
                {booking.customerName || booking.userName || "N/A"}
              </span>
            </div>
            
            {booking.eventDate && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  {t("MarkBusy.event_date", "Event Date")}
                </span>
                <span className={styles.infoValue}>
                  {new Date(booking.eventDate).toLocaleString()}
                </span>
              </div>
            )}
            {booking.eventLocation && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  {t("MarkBusy.event_location", "Event Location")}
                </span>
                <span className={styles.infoValue}>
                  {booking.eventLocation}
                </span>
              </div>
            )}
            {booking.eventType && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  {t("MarkBusy.event_type", "Event Type")}
                </span>
                <span className={styles.infoValue}>
                  {booking.eventType}
                </span>
              </div>
            )}
          </div>
        );
        showInfoModal(
          t("MarkBusy.booking_details", "Booking Details"),
          bookingContent,
        );
      }
      return;
    }

    if (vehicleBusyDates.includes(d)) {
      const busyDate = busyDates.find((bd) => bd.date === d);
      if (busyDate) {
        showViewBusyModal(busyDate);
      }
      return;
    }

    setUnavailableDate(date);
  };

  const handleDeleteBusyDate = async () => {
    try {
      const vehicle = JSON.parse(selectedVehicle);
      const apiType = vehicle.type;
      const busyDate = viewBusyModal.busyDate;

      if (!busyDate.exceptionId) {
        showPopup(
          t("MarkBusy.error_deleting", "Error: Exception ID not found"),
          "error",
        );
        closeViewBusyModal();
        return;
      }

      console.log("Deleting exception with ID:", busyDate.exceptionId);

      const response = await deleteAvailabilityException(busyDate.exceptionId);
      console.log("Delete response:", response);

      // Refresh busy dates
      await fetchBusyDates(vehicle, apiType);

      // Refresh overview data if in overview mode
      if (viewMode === "overview") {
        await fetchAllServicesAndBusyDates();
      }

      showPopup(
        t("MarkBusy.deleted_successfully", "Busy mark removed successfully!"),
        "success",
      );
      closeViewBusyModal();
    } catch (error) {
      console.error("Error deleting busy date:", error);
      showPopup(
        t(
          "MarkBusy.error_deleting",
          "Failed to remove busy mark. Please try again.",
        ),
        "error",
      );
      closeViewBusyModal();
    }
  };

  const handleAvailabilitySubmit = async (e) => {
    e.preventDefault();

    if (!serviceType) {
      showPopup(
        t("MarkBusy.select_service_type", "Please select service type"),
        "error",
      );
      return;
    }

    if (!selectedVehicle) {
      showPopup(
        t("MarkBusy.select_vehicle", "Please select a vehicle"),
        "error",
      );
      return;
    }

    if (!unavailableDate) {
      showPopup(t("MarkBusy.select_date", "Please select a date"), "error");
      return;
    }

    if (!reasonType || reasonType.trim() === "") {
      showPopup(
        t(
          "MarkBusy.reason_required",
          "Please select a reason for marking as busy",
        ),
        "error",
      );
      return;
    }

    if (reasonType === "other" && (!customReason || customReason.trim() === "")) {
      showPopup(
        t(
          "MarkBusy.custom_reason_required",
          "Please provide details for marking as busy",
        ),
        "error",
      );
      return;
    }

    setSubmitting(true);
    try {
      const vehicle = JSON.parse(selectedVehicle);

      const year = unavailableDate.getFullYear();
      const month = unavailableDate.getMonth();
      const day = unavailableDate.getDate();

      const startDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      const endDate = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));

      const finalReason = reasonType === "other" ? customReason.trim() : reasonType;
      
      const exceptionData = {
        unavailableFrom: startDate.toISOString(),
        unavailableTo: endDate.toISOString(),
        reason: finalReason,
      };

      const apiType = vehicle.type;

      await addServiceAvailabilityException(apiType, vehicle.id, exceptionData);

      await fetchBusyDates(vehicle, apiType);

      showPopup(
        t("MarkBusy.marked_busy", "Successfully marked as busy!"),
        "success",
      );

      // Refresh overview data if in overview mode
      if (viewMode === "overview") {
        await fetchAllServicesAndBusyDates();
      }

      setUnavailableDate(null);
      setReasonType("");
      setCustomReason("");
    } catch (error) {
      console.error("Error submitting availability:", error);
      showPopup(
        t(
          "MarkBusy.error_marking_busy",
          "Failed to mark as busy. Please try again.",
        ),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const serviceTypeOptions = [
    { value: "EVENT", label: t("MarkBusy.listing_service", "Listing Service") },
  ];

  const vehicleOptions =
    vehicles.length === 0
      ? [
          {
            value: "",
            label: t("MarkBusy.no_listing_services", "No listing services found"),
            disabled: true,
          },
        ]
      : vehicles.map((vehicle, index) => ({
          value: JSON.stringify(vehicle),
          label: vehicle.displayName,
        }));

  return (
    <div className={styles.pageContainer}>
      <AppHeader title={t("MarkBusy.title", "Mark Busy")} showBack={true} />

      {popup && (
        <Popup message={popup.message} type={popup.type} onClose={closePopup} />
      )}

      <InfoModal
        isOpen={infoModal.isOpen}
        onClose={closeInfoModal}
        title={infoModal.title}
      >
        {infoModal.content}
      </InfoModal>

      <ViewBusyModal
        isOpen={viewBusyModal.isOpen}
        onClose={closeViewBusyModal}
        busyDate={viewBusyModal.busyDate}
        onDelete={handleDeleteBusyDate}
      />

      <div className={styles.contentWrapper}>
        {/* Mode Toggle Buttons */}
        <div className={styles.modeToggle}>
          <button
            type="button"
            className={`${styles.modeButton} ${viewMode === "overview" ? styles.modeButtonActive : ""}`}
            onClick={() => setViewMode("overview")}
          >
            <FaCalendarAlt size={16} />
            {t("MarkBusy.view_all_busy", "View All Busy Dates")}
          </button>
          <button
            type="button"
            className={`${styles.modeButton} ${viewMode === "add" ? styles.modeButtonActive : ""}`}
            onClick={() => setViewMode("add")}
          >
            <FaBan size={16} />
            {t("MarkBusy.add_busy", "Add Busy Date")}
          </button>
        </div>

        {/* Overview Mode - Calendar with all busy dates */}
        {viewMode === "overview" && (
          <div className={styles.overviewSection}>
            {loadingOverview ? (
              <div className={styles.loadingContainer}>
                <Spinner text={t("MarkBusy.loading_busy_dates", "Loading busy dates...")} />
              </div>
            ) : (
              <>
                <div className={styles.overviewHeader}>
                  <h3 className={styles.overviewTitle}>
                    {t("MarkBusy.all_busy_dates", "All Busy Dates")}
                  </h3>
                  <p className={styles.overviewDescription}>
                    {t("MarkBusy.overview_description", "Click on highlighted dates to view details")}
                  </p>
                </div>

                <div className={styles.statsRow}>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{allServices.length}</div>
                    <div className={styles.statLabel}>{t("MarkBusy.total_services", "Total Services")}</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{allBusyDates.length}</div>
                    <div className={styles.statLabel}>{t("MarkBusy.total_busy_marks", "Busy Marks")}</div>
                  </div>
                </div>

                <div className={styles.calendarWrapper}>
                  <Calendar
                    value={selectedDateInOverview}
                    onClickDay={(date) => {
                      setSelectedDateInOverview(date);
                      const dateStr = formatDate(date);
                      const busyOnDate = allBusyDates.filter(bd => bd.date === dateStr);
                      if (busyOnDate.length > 0) {
                        // Show details for first busy mark
                        showViewBusyModal(busyOnDate[0]);
                      }
                    }}
                    tileClassName={({ date, view }) => {
                      if (view === "month") {
                        const dateStr = formatDate(date);
                        const hasBusy = allBusyDates.some(bd => bd.date === dateStr);
                        if (hasBusy) {
                          return "overview-busy-date";
                        }
                      }
                      return null;
                    }}
                  />
                </div>

                <div className={styles.legend}>
                  <div className={styles.legendItem}>
                    <span
                      className={styles.legendDot}
                      style={{ background: "#fbbf24" }}
                    ></span>
                    <span>{t("MarkBusy.busy_dates", "Busy Dates")}</span>
                  </div>
                </div>

                {/* List of busy dates */}
                {allBusyDates.length > 0 && (
                  <div className={styles.busyDatesList}>
                    <h4 className={styles.listTitle}>
                      {t("MarkBusy.busy_dates_list", "Busy Dates List")}
                    </h4>
                    {allBusyDates.map((busyDate, index) => (
                      <div
                        key={index}
                        className={styles.busyDateItem}
                        onClick={() => showViewBusyModal(busyDate)}
                      >
                        <div className={styles.busyDateInfo}>
                          <div className={styles.busyDateDate}>
                            <FaCalendarAlt size={14} />
                            {busyDate.date}
                          </div>
                          <div className={styles.busyDateService}>
                            {busyDate.serviceName}
                          </div>
                          <div className={styles.busyDateReason}>
                            {busyDate.reason}
                          </div>
                        </div>
                        <FaInfoCircle size={16} color="#667eea" />
                      </div>
                    ))}
                  </div>
                )}

                {allBusyDates.length === 0 && (
                  <div className={styles.emptyState}>
                    <FaCalendarAlt size={48} color="#cbd5e1" />
                    <p>{t("MarkBusy.no_busy_dates", "No busy dates marked yet")}</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Add Mode - Existing form */}
        {viewMode === "add" && (
          <>
            <div className={styles.pageHeader}>
              <div className={styles.iconWrapper}>
                <FaBan size={24} />
              </div>
              <h2 className={styles.pageTitle}>
                {t("MarkBusy.heading", "Mark Service as Busy")}
              </h2>
              <p className={styles.pageDescription}>
                {t(
                  "MarkBusy.description",
                  "Select a service and date to mark as unavailable",
                )}
              </p>
            </div>

            <form onSubmit={handleAvailabilitySubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              {t("MarkBusy.service_type", "Service Type")}
            </label>
            <CustomDropdown
              value={serviceType}
              onChange={(value) => {
                setServiceType(value);
                setSelectedVehicle("");
                setUnavailableDate(null);
              }}
              options={serviceTypeOptions}
              placeholder={t(
                "MarkBusy.choose_service",
                "Select service type...",
              )}
            />
          </div>

          {serviceType && (
            <div className={styles.formGroup}>
              <label className={styles.label}>
                {t("MarkBusy.select_listing_service", "Select Listing Service")}
              </label>

              {loadingVehicles ? (
                <div className={styles.loadingContainer}>
                  <Spinner
                    text={t("MarkBusy.loading_vehicles", "Loading...")}
                  />
                </div>
              ) : (
                <>
                  <CustomDropdown
                    value={selectedVehicle}
                    onChange={setSelectedVehicle}
                    options={vehicleOptions}
                    placeholder={t("MarkBusy.choose_listing_service", "Select listing service...")}
                    disabled={vehicles.length === 0}
                  />

                  {selectedVehicle &&
                    (() => {
                      try {
                        const vehicle = JSON.parse(selectedVehicle);
                        return vehicle.image ? (
                          <div className={styles.vehiclePreview}>
                            <img
                              src={vehicle.image}
                              alt={vehicle.displayName}
                              className={styles.vehicleImage}
                            />
                            <div className={styles.vehicleInfo}>
                              <span className={styles.vehicleName}>
                                {vehicle.displayName}
                              </span>
                            </div>
                          </div>
                        ) : null;
                      } catch (e) {
                        console.error("Error parsing selected vehicle:", e);
                        return null;
                      }
                    })()}
                </>
              )}
            </div>
          )}

          {selectedVehicle && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaCalendarAlt size={14} />
                  {t("MarkBusy.select_date", "Select Date")}
                </label>
                <div className={styles.calendarWrapper}>
                  <Calendar
                    minDate={new Date()}
                    value={unavailableDate}
                    onClickDay={handleDateClickInModal}
                    tileClassName={tileClassNameModal}
                    tileDisabled={tileDisabled}
                  />
                </div>

                <div className={styles.legend}>
                  <div className={styles.legendItem}>
                    <span
                      className={styles.legendDot}
                      style={{ background: "#fee2e2" }}
                    ></span>
                    <span>{t("MarkBusy.has_booking", "Has Booking")}</span>
                  </div>
                  <div className={styles.legendItem}>
                    <span
                      className={styles.legendDot}
                      style={{ background: "#fef3c7" }}
                    ></span>
                    <span>
                      {t(
                        "MarkBusy.already_busy",
                        "Already Busy (Click to View)",
                      )}
                    </span>
                  </div>
                  <div className={styles.legendItem}>
                    <span
                      className={styles.legendDot}
                      style={{ background: "#10b981" }}
                    ></span>
                    <span>{t("MarkBusy.selected", "Selected")}</span>
                  </div>
                </div>

                <div className={styles.infoNote}>
                  <FaInfoCircle size={14} />
                  <span>
                    {t(
                      "MarkBusy.click_info",
                      "Click on busy dates to view details and delete if needed",
                    )}
                  </span>
                </div>
              </div>

              {unavailableDate && (
                <div className={styles.selectedDateInfo}>
                  <FaCalendarAlt size={16} color="#10b981" />
                  <div>
                    <span className={styles.infoLabel}>
                      {t("MarkBusy.selected_date", "Selected Date")}
                    </span>
                    <span className={styles.infoValue}>
                      {formatDate(unavailableDate)}
                    </span>
                  </div>
                </div>
              )}

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  {t("MarkBusy.reason_mandatory", "Reason")}{" "}
                  <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <CustomDropdown
                  value={reasonType}
                  onChange={(value) => {
                    setReasonType(value);
                    if (value !== "other") {
                      setCustomReason("");
                    }
                  }}
                  options={[
                    { value: "External Booking", label: t("MarkBusy.reason_external", "External Booking") },
                    { value: "Booking from Bhada24", label: t("MarkBusy.reason_bhada24", "Booking from Bhada24") },
                    { value: "Personal Work", label: t("MarkBusy.reason_personal", "Personal Work") },
                    { value: "Maintenance", label: t("MarkBusy.reason_maintenance", "Maintenance") },
                    { value: "Not Available", label: t("MarkBusy.reason_not_available", "Not Available") },
                    { value: "other", label: t("MarkBusy.reason_other", "Other (Specify Below)") },
                  ]}
                  placeholder={t(
                    "MarkBusy.select_reason",
                    "Select reason...",
                  )}
                />
              </div>

              {reasonType === "other" && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    {t("MarkBusy.custom_reason", "Specify Reason")}{" "}
                    <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <textarea
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder={t(
                      "MarkBusy.custom_reason_placeholder",
                      "Please provide details...",
                    )}
                    className={styles.input}
                    required
                    rows={4}
                  />
                </div>
              )}

              <div className={styles.buttonGroup}>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={
                    submitting || !unavailableDate || !reasonType ||
                    (reasonType === "other" && !customReason.trim())
                  }
                >
                  {submitting ? (
                    <span className={styles.buttonContent}>
                      <div className={styles.spinner}></div>
                      {t("MarkBusy.submitting", "Submitting...")}
                    </span>
                  ) : (
                    <span className={styles.buttonContent}>
                      <FaBan size={14} />
                      {t("MarkBusy.mark_busy", "Mark as Busy")}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => navigate(-1)}
                  disabled={submitting}
                >
                  {t("MarkBusy.cancel", "Cancel")}
                </button>
              </div>
            </>
          )}
        </form>
        </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}